<template>
  <PrimeVueDataTable
    v-model:filters="filters"
    v-model:selection="selectedNews"
    :loading="loading"
    class="p-datatable-ir"
    :style="loading ? 'min-height: 300px;' : null"
    selection-mode="single"
    :paginator="!loading"
    :rows="10"
    :value="filteredNews"
    :global-filter-fields="['title']"
    filter-display="row"
    @row-select="showModal = true"
  >
    <template #empty>
      <DictionaryRichText
        v-if="!loading"
        dictionary-key="ir.news.zeroresults"
        fallback="ir.news.zeroresults"
      />
    </template>
    <template #loading>
      <InvestorRelationsLoadingSkeleton v-if="loading" style="height: 300px" />
    </template>
    <PrimeVueColumn
      v-if="!loading"
      field="date"
      :show-filter-menu="false"
      style="width: 15%"
    >
      <template #body="{ data }">
        <strong>{{ data.date }}</strong>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <PrimeVueSelect
          v-model="filterModel.value"
          :default-value="years[0]"
          :options="years"
          :placeholder="
            $textDictionary['ir.news.select.year'] ?? 'ir.news.select.year'
          "
          class="p-select-ir"
          variant="filled"
          style="min-width: 12rem"
          :show-clear="true"
          size="small"
          @change="filterCallback()"
        >
        </PrimeVueSelect>
      </template>
    </PrimeVueColumn>
    <PrimeVueColumn
      v-if="!loading"
      field="title"
      :show-filter-menu="false"
      style="width: 85%"
    >
      <template #filter>
        <PrimeVueForm
          class="p-search-box-form p-search-box-form-ir col-lg-6 col-sm-12"
        >
          <PrimeVueInputText
            v-model="filters['global'].value"
            :placeholder="
              $textDictionary['ir.news.search.placeholder'] ??
              'ir.news.search.placeholder'
            "
            class="w-100"
            size="small"
            autocomplete="off"
            type="search"
            name="search-value"
          />
        </PrimeVueForm>
      </template>
      <template #body="{ data }">
        {{ data.title }}
      </template>
    </PrimeVueColumn>
    <template
      #paginatorcontainer="{
        page,
        pageCount,
        prevPageCallback,
        nextPageCallback,
        changePageCallback
      }"
    >
      <nav
        v-if="pageCount && pageCount > 1 && !loading"
        class="product-list-pagination__nav"
      >
        <ul class="product-list-pagination__list">
          <!-- PREV -->
          <li
            v-if="page !== 0"
            class="product-list-pagination__list-item product-list-pagination__list-item--prev"
          >
            <a
              href="javascript:"
              class="product-list-pagination__link product-list-pagination__link--prev"
              @click="prevPageCallback"
            >
              <span class="visually-hidden">Prev</span>
            </a>
          </li>
          <li v-if="page - 2 >= 1" class="product-list-pagination__list-item">
            <a
              href="javascript:"
              class="product-list-pagination__link"
              @click.prevent="changePageCallback(page - 2)"
            >
              {{ page - 1 }}
            </a>
          </li>
          <li v-if="page - 1 >= 1" class="product-list-pagination__list-item">
            <a
              href="javascript:"
              class="product-list-pagination__link"
              @click.prevent="changePageCallback(page - 1)"
            >
              {{ page }}
            </a>
          </li>
          <li
            class="product-list-pagination__list-item product-list-pagination__list-item--active"
          >
            <a
              href="javascript:"
              class="product-list-pagination__link product-list-pagination__link--active"
            >
              {{ page + 1 }}
            </a>
          </li>
          <li class="product-list-pagination__list-item">
            <a
              v-if="pageCount && page + 2 <= pageCount"
              href="javascript:"
              class="product-list-pagination__link"
              @click.prevent="changePageCallback(page + 1)"
            >
              {{ page + 2 }}
            </a>
          </li>
          <li class="product-list-pagination__list-item">
            <a
              v-if="pageCount && page + 3 <= pageCount"
              href="javascript:"
              class="product-list-pagination__link"
              @click.prevent="changePageCallback(page + 2)"
            >
              {{ page + 3 }}
            </a>
          </li>
          <!-- NEXT -->
          <li
            v-if="pageCount && !(page === pageCount - 1)"
            class="product-list-pagination__list-item product-list-pagination__list-item--next"
          >
            <a
              href="javascript:"
              class="product-list-pagination__link product-list-pagination__link--next"
              @click.prevent="nextPageCallback"
            >
              <span class="visually-hidden">Next</span>
            </a>
          </li>
        </ul>
      </nav>
    </template>
  </PrimeVueDataTable>

  <ModalModule
    v-if="selectedNews"
    v-model="showModal"
    :title="selectedNews.title ? selectedNews.title : ''"
    close-icon-text="close"
    :is-full-screen="true"
    :esc-to-close="true"
    :click-to-close="true"
    @close="
      showModal = false;
      clearNews();
    "
    @abort="
      showModal = false;
      clearNews();
    "
  >
    <div class="col-lg-9 col-sm-12">
      <div class="rte-text ir-text" v-html="selectedNews?.content" />
      <LinkElement
        :link="selectedNews?.url"
        icon-name="link"
        icon-type="icon-right"
        :target-blank="true"
      >
        {{ $textDictionary['ir.news.openInNewTab'] ?? 'ir.news.openInNewTab' }}
      </LinkElement>
    </div>
  </ModalModule>
  <div
    v-if="!news && configuration.fallbackUrl && !loading"
    class="text-center"
  >
    {{
      $textDictionary['ir.news.noresults.fallbackmassage'] ??
      'ir.news.noresults.fallbackmessage'
    }}
    <LinkElement
      :link="configuration.fallbackUrl"
      icon-name="link"
      class="text-left"
      icon-type="icon-right"
      :target-blank="true"
    >
      {{
        $textDictionary['ir.news.noresults.fallbacklink'] ??
        'ir.news.noresults.fallbacklink'
      }}
    </LinkElement>
  </div>
</template>

<script setup lang="ts">
import { LinkElement, ModalModule } from '@hypercodestudio/basler-components';
import { v } from 'h3-valibot';
import { parseURL } from '~/utils/validation/parseURL';
import type {
  ITechnicalComponent,
  LOCALE_CODE
} from '~~/lib/ContentfulService';
import { FilterMatchMode } from '@primevue/core/api';
import { mapToFormattedDate } from '~/utils/mapper/mapToFormattedDate';
import type { EQSItem, EQSResponse, NewsEntry } from '~/types/IrTypes';
import InvestorRelationsLoadingSkeleton from '~/components/global/lib/Ir/InvestorRelationsLoadingSkeleton.vue';
import { isDefined } from '~/utils/guards/isDefined';

interface Props {
  item: ITechnicalComponent;
}

const filters = ref();
const showModal = ref(false);
const selectedNews = ref();
const { $activeLocale, $textDictionary } = useNuxtApp();
const years: Ref<string[]> = ref([]);
const news = ref();
const filteredNews = ref();
const loading = ref(true);

const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    title: { value: null, matchMode: FilterMatchMode.CONTAINS },
    date: {
      value: years.value ? years.value[0] : null,
      matchMode: FilterMatchMode.CONTAINS
    }
  };
};

initFilters();

const _clearFilter = () => {
  initFilters();
};

const props = defineProps<Props>();
const requestUrl = useRequestURL();

const configurationSchema = v.object({
  url: parseURL,
  category: v.optional(v.string()),
  fallbackUrl: v.optional(v.string()),
  shownYears: v.optional(v.number())
});

const configuration = computed(() => {
  const parsed = v.parse(configurationSchema, props.item.fields.configuration);

  requestUrl.searchParams?.forEach((value, key) => {
    parsed.url.searchParams.append(key, value);
  });

  return parsed;
});

onMounted(async () => {
  const { data: eqsResponse } = await useFetch<EQSResponse>(
    '/api/ir/getIrNews',
    {
      query: {
        jsonUrl: configuration.value.url.href,
        category: configuration.value.category,
        locale: $activeLocale.value.webapp
      }
    }
  );

  const shownYears = configuration.value.shownYears ?? 6;

  if (eqsResponse?.value?.items) {
    const yearCollector: Record<number, boolean> = {};
    news.value = await Promise.all(
      eqsResponse.value.items.map(async (item: EQSItem) => {
        const date = new Date(item.date_published.toString());
        const max_age = new Date(Date.now());

        if (date.getFullYear() >= max_age.getFullYear() - shownYears) {
          if (!(date.getFullYear() in yearCollector)) {
            yearCollector[date.getFullYear()] = true;
          }
          const formattedDate = mapToFormattedDate(
            date.toString(),
            $activeLocale.value.webapp as LOCALE_CODE
          );
          return {
            title: item.title,
            date: formattedDate,
            dateTimestamp: date.getTime(),
            content: item.content_html,
            url: item.url
          } satisfies NewsEntry;
        }
      })
    );
    filteredNews.value = news.value.filter(isDefined);
    years.value = Object.keys(yearCollector).sort(function (
      a: string,
      b: string
    ) {
      return Number(b) - Number(a);
    });
    loading.value = false;
  }
  initFilters();
});
function clearNews() {
  selectedNews.value = {};
}
</script>

<style>
.news_footer_layout,
.news_layout_footer_end {
  font-size: small;
}
.news_footer_layout tr td:first-child,
.news_layout_footer_end tr td:first-child {
  font-weight: bold;
  padding-right: 10px;
}
.news_layout_data_table td {
  padding: 5px 10px;
  border: 1px solid var(--bslr-pagination-border-color);
}
.ir-text pre {
  white-space: break-spaces;
}
.ir-text pre b {
  display: block;
}
@media (max-width: 768px) {
  .p-datatable-thead tr:last-child th:first-child {
    padding-bottom: 0.5rem;
    border: none;
  }
  .p-datatable-thead th,
  .p-datatable-tbody td {
    display: block;
    width: 100% !important;
  }
  .p-datatable-tbody td:first-child {
    padding-bottom: 0.5rem;
    border: none;
  }
  .p-select-ir {
    width: 100%;
  }
}
</style>
